import {
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
  useState,
} from 'react';
import {
  Point,
  Line,
  angleBetweenLinesDeg,
  openingAngleDeg,
  dist,
  midpoint,
  lineLineIntersection,
} from '../lib/geometry';
import { usePinchPan } from '../hooks/usePinchPan';
import { useViewportResize } from '../hooks/useViewportResize';

export type AngleMode = 'free' | 'shared';

export interface AnnotationCanvasHandle {
  /** Returns a flattened canvas (image + annotation) ready for export. */
  flatten: () => HTMLCanvasElement;
  reset: () => void;
}

interface Props {
  /** The captured frame, in its full intrinsic resolution. */
  source: HTMLCanvasElement;
  /** Free = 2 independent lines (4 points). Shared = 1 vertex + 2 arms (3 points). */
  mode: AngleMode;
  /** Live angle in degrees (read by the parent for the HUD). */
  onAngleChange?: (deg: number) => void;
  /** Color of the two lines and labels. */
  colorA?: string;
  colorB?: string;
}

/* ─────────── State model ─────────── */

type FreeState = { mode: 'free'; a: Line; b: Line };
type SharedState = { mode: 'shared'; vertex: Point; arm1: Point; arm2: Point };
type AngleState = FreeState | SharedState;

/* ─────────── Drag handle keys ─────────── */

type FreeKey = 'aa' | 'ab' | 'ba' | 'bb';
type SharedKey = 'vertex' | 'arm1' | 'arm2';
type DragKey = FreeKey | SharedKey;

/* ─────────── Visual constants ─────────── */

const HANDLE_OUTER = 11; // outer ring radius (CSS px)
const HANDLE_DOT = 2.5;
const VERTEX_RING = 16; // slightly bigger ring on the shared vertex so it stands out
const HIT_RADIUS = 34; // touch hit zone (CSS px)

/* ────────────────────────────────────────────────────────────── */

const AnnotationCanvas = forwardRef<AnnotationCanvasHandle, Props>(
  function AnnotationCanvas(
    { source, mode, onAngleChange, colorA = '#7cc4ff', colorB = '#ff9f0a' },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const dragRef = useRef<DragKey | null>(null);

    // Bumps on viewport resize / orientation change so the render effect
    // below re-measures the container and redraws at the new size.
    // Without this the canvas stays at the size we first saw and the
    // image renders stretched or with empty bars after a rotation.
    const viewportTick = useViewportResize();

    const [state, setState] = useState<AngleState>(() =>
      initialState(mode, source.width, source.height),
    );
    const stateRef = useRef(state);
    stateRef.current = state;

    const { transform, handlers, reset: resetTransform } = usePinchPan({
      minScale: 1,
      maxScale: 8,
      panSinglePointer: false,
    });

    /* When the parent flips the mode prop, convert the existing geometry so
       the user keeps approximately the same lines / vertex they already drew. */
    useEffect(() => {
      setState((cur) => (cur.mode === mode ? cur : convertMode(cur, mode, source)));
    }, [mode, source]);

    /* ─── Coordinate conversion ─── */
    const screenToImage = (sx: number, sy: number): Point => {
      const rect = containerRef.current!.getBoundingClientRect();
      const fit = fitContain(source.width, source.height, rect.width, rect.height);
      const cssX = (sx - rect.left - transform.tx) / transform.scale - fit.x;
      const cssY = (sy - rect.top - transform.ty) / transform.scale - fit.y;
      return {
        x: cssX * (source.width / fit.w),
        y: cssY * (source.height / fit.h),
      };
    };

    /* ─── Draw loop ─── */
    useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const rect = container.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;

      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      const ctx = canvas.getContext('2d')!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, cssW, cssH);

      const fit = fitContain(source.width, source.height, cssW, cssH);
      ctx.save();
      ctx.translate(transform.tx, transform.ty);
      ctx.scale(transform.scale, transform.scale);
      ctx.drawImage(source, fit.x, fit.y, fit.w, fit.h);

      const toCss = (p: Point) => ({
        x: fit.x + (p.x / source.width) * fit.w,
        y: fit.y + (p.y / source.height) * fit.h,
      });

      if (state.mode === 'free') {
        drawLine(ctx, toCss(state.a.a), toCss(state.a.b), colorA, transform.scale);
        drawLine(ctx, toCss(state.b.a), toCss(state.b.b), colorB, transform.scale);
        drawHandle(ctx, toCss(state.a.a), colorA, transform.scale);
        drawHandle(ctx, toCss(state.a.b), colorA, transform.scale);
        drawHandle(ctx, toCss(state.b.a), colorB, transform.scale);
        drawHandle(ctx, toCss(state.b.b), colorB, transform.scale);
      } else {
        drawLine(ctx, toCss(state.vertex), toCss(state.arm1), colorA, transform.scale);
        drawLine(ctx, toCss(state.vertex), toCss(state.arm2), colorB, transform.scale);
        drawHandle(ctx, toCss(state.arm1), colorA, transform.scale);
        drawHandle(ctx, toCss(state.arm2), colorB, transform.scale);
        drawVertex(ctx, toCss(state.vertex), transform.scale);
      }

      ctx.restore();
    }, [state, transform, source, colorA, colorB, viewportTick]);

    /* ─── Angle output ─── */
    useEffect(() => {
      const deg =
        state.mode === 'free'
          ? angleBetweenLinesDeg(state.a, state.b)
          : openingAngleDeg(state.vertex, state.arm1, state.arm2);
      onAngleChange?.(deg);
    }, [state, onAngleChange]);

    /* ─── Pointer handling ─── */
    const onPointerDown = (e: React.PointerEvent) => {
      const img = screenToImage(e.clientX, e.clientY);
      const rect = containerRef.current!.getBoundingClientRect();
      const fit = fitContain(source.width, source.height, rect.width, rect.height);
      const hitImgRadius =
        (HIT_RADIUS / transform.scale) * (source.width / fit.w);
      const hit = hitTest(stateRef.current, img, hitImgRadius);
      if (hit) {
        dragRef.current = hit;
        (e.target as Element).setPointerCapture?.(e.pointerId);
        return;
      }
      handlers.onPointerDown(e);
    };

    const onPointerMove = (e: React.PointerEvent) => {
      if (dragRef.current) {
        const img = screenToImage(e.clientX, e.clientY);
        setState((cur) => updatePoint(cur, dragRef.current!, img));
        return;
      }
      handlers.onPointerMove(e);
    };

    const onPointerUp = (e: React.PointerEvent) => {
      if (dragRef.current) {
        dragRef.current = null;
        return;
      }
      handlers.onPointerUp(e);
    };

    /* ─── Imperative API ─── */
    useImperativeHandle(ref, () => ({
      flatten: () =>
        flattenToCanvas(source, stateRef.current, colorA, colorB),
      reset: () => {
        setState(initialState(mode, source.width, source.height));
        resetTransform();
      },
    }));

    return (
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          background: '#000',
          overflow: 'hidden',
          touchAction: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <canvas ref={canvasRef} style={{ display: 'block' }} />
      </div>
    );
  },
);

export default AnnotationCanvas;

/* ════════════════════════ helpers ════════════════════════ */

function initialState(mode: AngleMode, w: number, h: number): AngleState {
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.32;
  if (mode === 'free') {
    return {
      mode: 'free',
      a: { a: { x: cx - r, y: cy - r * 0.2 }, b: { x: cx + r, y: cy - r * 0.2 } },
      b: { a: { x: cx - r * 0.7, y: cy + r * 0.4 }, b: { x: cx + r * 0.7, y: cy - r * 0.4 } },
    };
  }
  return {
    mode: 'shared',
    vertex: { x: cx, y: cy },
    arm1: { x: cx - r, y: cy + r * 0.4 },
    arm2: { x: cx + r, y: cy + r * 0.4 },
  };
}

/** Convert geometry between modes preserving as much intent as possible. */
function convertMode(
  cur: AngleState,
  next: AngleMode,
  source: HTMLCanvasElement,
): AngleState {
  if (cur.mode === next) return cur;

  if (next === 'shared') {
    // free → shared: vertex = intersection of the two lines (or midpoint
    // of their nearest endpoints if parallel). Arms = the far ends of each line.
    const free = cur as FreeState;
    const inter = lineLineIntersection(free.a, free.b);
    const vertex =
      inter ?? midpoint(free.a.a, free.b.a); // fallback for parallel lines
    const arm1 =
      dist(vertex, free.a.a) > dist(vertex, free.a.b) ? free.a.a : free.a.b;
    const arm2 =
      dist(vertex, free.b.a) > dist(vertex, free.b.b) ? free.b.a : free.b.b;

    // Clamp inside the image just in case the intersection is way off-canvas.
    return {
      mode: 'shared',
      vertex: clamp(vertex, source),
      arm1: clamp(arm1, source),
      arm2: clamp(arm2, source),
    };
  }

  // shared → free: each arm becomes a full line ending at the vertex.
  const sh = cur as SharedState;
  return {
    mode: 'free',
    a: { a: sh.vertex, b: sh.arm1 },
    b: { a: sh.vertex, b: sh.arm2 },
  };
}

function clamp(p: Point, source: HTMLCanvasElement): Point {
  return {
    x: Math.max(0, Math.min(source.width, p.x)),
    y: Math.max(0, Math.min(source.height, p.y)),
  };
}

function updatePoint(state: AngleState, key: DragKey, p: Point): AngleState {
  if (state.mode === 'free') {
    switch (key) {
      case 'aa': return { ...state, a: { ...state.a, a: p } };
      case 'ab': return { ...state, a: { ...state.a, b: p } };
      case 'ba': return { ...state, b: { ...state.b, a: p } };
      case 'bb': return { ...state, b: { ...state.b, b: p } };
      default: return state;
    }
  }
  switch (key) {
    case 'vertex': return { ...state, vertex: p };
    case 'arm1':   return { ...state, arm1: p };
    case 'arm2':   return { ...state, arm2: p };
    default:       return state;
  }
}

function fitContain(srcW: number, srcH: number, dstW: number, dstH: number) {
  const s = Math.min(dstW / srcW, dstH / srcH);
  const w = srcW * s;
  const h = srcH * s;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}

function hitTest(state: AngleState, p: Point, radius: number): DragKey | null {
  type Cand = { key: DragKey; pt: Point };
  const cands: Cand[] =
    state.mode === 'free'
      ? [
          { key: 'aa', pt: state.a.a },
          { key: 'ab', pt: state.a.b },
          { key: 'ba', pt: state.b.a },
          { key: 'bb', pt: state.b.b },
        ]
      : [
          { key: 'vertex', pt: state.vertex },
          { key: 'arm1', pt: state.arm1 },
          { key: 'arm2', pt: state.arm2 },
        ];
  let best: DragKey | null = null;
  let bestD = radius;
  for (const c of cands) {
    const d = dist(c.pt, p);
    if (d < bestD) {
      bestD = d;
      best = c.key;
    }
  }
  return best;
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  a: Point,
  b: Point,
  color: string,
  scale: number,
) {
  ctx.save();
  ctx.lineCap = 'round';
  // Dark backing stroke — kept just thick enough to stay visible against
  // bright fluoroscopy areas, without obscuring the underlying anatomy.
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = 3.5 / scale;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  // Coloured line — thin so the user can position the endpoint precisely
  // against fine anatomic landmarks.
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6 / scale;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  // Subtle midpoint tick — kept small.
  const m = midpoint(a, b);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(m.x, m.y, 2 / scale, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawHandle(
  ctx: CanvasRenderingContext2D,
  p: Point,
  color: string,
  scale: number,
) {
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, (HANDLE_OUTER + 1.5) / scale, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 / scale;
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_OUTER / scale, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(p.x, p.y, (HANDLE_DOT + 1.2) / scale, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_DOT / scale, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Distinct visual for the shared vertex — neutral white, larger ring. */
function drawVertex(
  ctx: CanvasRenderingContext2D,
  p: Point,
  scale: number,
) {
  ctx.save();
  // Dark halo
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, (VERTEX_RING + 1.5) / scale, 0, Math.PI * 2);
  ctx.fill();

  // White ring
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2 / scale;
  ctx.beginPath();
  ctx.arc(p.x, p.y, VERTEX_RING / scale, 0, Math.PI * 2);
  ctx.stroke();

  // Inner contrast ring
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 1 / scale;
  ctx.beginPath();
  ctx.arc(p.x, p.y, (VERTEX_RING - 2) / scale, 0, Math.PI * 2);
  ctx.stroke();

  // Centre dot
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_DOT / scale, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ─── Export / flatten ─── */

function flattenToCanvas(
  source: HTMLCanvasElement,
  state: AngleState,
  colorA: string,
  colorB: string,
): HTMLCanvasElement {
  // Returns the captured frame with the annotation drawn on top — clean,
  // without any branding, label or watermark. Those live in the report frame
  // so the standalone annotation can be reused (saved locally, embedded, etc.)
  // with consistent CADtomie chrome only when wrapped via buildAngleReport.
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d')!;
  ctx.drawImage(source, 0, 0);

  if (state.mode === 'free') {
    drawLine(ctx, state.a.a, state.a.b, colorA, 1);
    drawLine(ctx, state.b.a, state.b.b, colorB, 1);
  } else {
    drawLine(ctx, state.vertex, state.arm1, colorA, 1);
    drawLine(ctx, state.vertex, state.arm2, colorB, 1);
    drawVertex(ctx, state.vertex, 1);
  }

  return out;
}

